// Logique financière calculée à la lecture (jamais stockée) : statut de paiement,
// caisse, retards, TVA, montant en lettres.
import { calculateTotalAvances, convertFirestoreTimestamp } from './helpers.js';

export const PAYMENT_STATUS = {
  NON_DEFINI: 'non_defini',
  NON_PAYE: 'non_paye',
  PARTIEL: 'partiel',
  SOLDE: 'solde'
};

export const PAYMENT_STATUS_LABELS = {
  non_defini: 'Non défini',
  non_paye: 'Non payé',
  partiel: 'Partiel',
  solde: 'Soldé'
};

export const PAYMENT_STATUS_STYLES = {
  non_defini: 'bg-slate-100 text-slate-600',
  non_paye: 'bg-red-50 text-red-700',
  partiel: 'bg-accent-50 text-accent-700',
  solde: 'bg-emerald-50 text-emerald-700'
};

export const MODES_PAIEMENT = [
  { value: 'espece', label: 'Espèces' },
  { value: 'cheque', label: 'Chèque' },
  { value: 'virement', label: 'Virement' }
];

export const CATEGORIES_DEPENSES = [
  'Loyer', 'Salaires', 'Fournitures', 'Déplacements', 'Sous-traitance (BET/Topo)',
  'Frais administratifs', 'Impôts et taxes', 'Télécom / Internet', 'Matériel', 'Autre'
];

// Statut de paiement d'un dossier (recalculé, jamais stocké)
export const getPaymentStatus = (project) => {
  const total = (parseFloat(project?.montantTotal) || 0) + (parseFloat(project?.fraisSupplementaires) || 0);
  const avances = calculateTotalAvances(project?.avances);
  if (total === 0) return PAYMENT_STATUS.NON_DEFINI;
  if (total - avances <= 0) return PAYMENT_STATUS.SOLDE;
  if (avances > 0) return PAYMENT_STATUS.PARTIEL;
  return PAYMENT_STATUS.NON_PAYE;
};

// Statuts « précoces » : au-delà du délai sans évolution, le dossier est en retard
export const EARLY_STATUSES = ['En cours', 'Plan validé par Client', 'Déposé', 'Défavorable'];
export const DEFAULT_DELAI_RETARD_JOURS = 15;

const toDate = (value) => {
  if (!value) return null;
  const d = convertFirestoreTimestamp(value);
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? null : date;
};

// Date du dernier changement de statut (historique, sinon création)
export const getLastStatusDate = (project) => {
  const history = project?.statusHistory || [];
  if (history.length > 0) {
    const last = history[history.length - 1];
    return toDate(last.date);
  }
  return toDate(project?.createdAt);
};

export const getDaysSinceStatus = (project, now = new Date()) => {
  const d = getLastStatusDate(project);
  if (!d) return 0;
  return Math.floor((now - d) / 86400000);
};

export const isProjectLate = (project, delaiJours = DEFAULT_DELAI_RETARD_JOURS, now = new Date()) => {
  const statut = project?.statut || 'En cours';
  if (!EARLY_STATUSES.includes(statut)) return false;
  return getDaysSinceStatus(project, now) > delaiJours;
};

// Caisse : solde = initial + Σ avances enCaisse − Σ dépenses depuisCaisse,
// en ne comptant que les mouvements ENREGISTRÉS après la (re)définition du montant initial
// (comparaison sur l'horodatage de création, pas sur la date métier du mouvement).
const dateKey = (value) => {
  const d = toDate(value);
  return d ? d.toISOString().slice(0, 10) : null;
};

const isAfterReset = (createdAt, resetAt) => {
  if (!resetAt) return true;
  const c = toDate(createdAt);
  const r = toDate(resetAt);
  if (!r) return true;
  if (!c) return false; // mouvement sans horodatage = antérieur
  return c.getTime() > r.getTime();
};

export const computeCaisse = ({ config, projects = [], depenses = [] }) => {
  const initialAmount = parseFloat(config?.initialAmount) || 0;
  const resetAt = config?.resetAt || null;
  let entrees = 0;
  const mouvements = [];

  projects.forEach((p) => {
    (p.avances || []).forEach((a) => {
      if (a.enCaisse !== true) return;
      if (!isAfterReset(a.createdAt, resetAt)) return;
      const key = dateKey(a.date);
      if (!key) return;
      const montant = parseFloat(a.montant) || 0;
      entrees += montant;
      mouvements.push({
        type: 'entree', date: key, montant, label: `Avance · ${p.client || p.refGlobal || 'dossier'}`,
        modePaiement: a.modePaiement || '', projectId: p.id, refGlobal: p.refGlobal,
        createdAtMs: toDate(a.createdAt)?.getTime() || 0
      });
    });
  });

  let sorties = 0;
  depenses.forEach((d) => {
    if (d.depuisCaisse !== true) return;
    if (!isAfterReset(d.createdAt, resetAt)) return;
    const key = dateKey(d.date);
    if (!key) return;
    const montant = parseFloat(d.montant) || 0;
    sorties += montant;
    mouvements.push({ type: 'sortie', date: key, montant, label: `${d.categorie || 'Dépense'} · ${d.description || ''}`, depenseId: d.id, createdAtMs: toDate(d.createdAt)?.getTime() || 0 });
  });

  // Plus récent en premier : date du mouvement, puis ordre d'enregistrement (dernier ajouté en tête)
  mouvements.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : b.createdAtMs - a.createdAtMs));
  return { initialAmount, resetAt, entrees, sorties, solde: initialAmount + entrees - sorties, mouvements };
};

// Stats dépenses par mois et par catégorie
export const computeDepensesStats = (depenses = []) => {
  const parMois = {};
  const parCategorie = {};
  let total = 0;
  depenses.forEach((d) => {
    const montant = parseFloat(d.montant) || 0;
    total += montant;
    const key = (dateKey(d.date) || '').slice(0, 7) || 'inconnu';
    parMois[key] = (parMois[key] || 0) + montant;
    const cat = d.categorie || 'Autre';
    parCategorie[cat] = (parCategorie[cat] || 0) + montant;
  });
  return { total, parMois, parCategorie };
};

// TVA
export const DEFAULT_TVA = 20;
export const computeFactureTotals = (lignes = [], tauxTVA = DEFAULT_TVA) => {
  const montantHT = lignes.reduce((sum, l) => sum + (parseFloat(l.quantite) || 0) * (parseFloat(l.prixUnitaire) || 0), 0);
  const montantTVA = Math.round(montantHT * (parseFloat(tauxTVA) || 0)) / 100;
  return { montantHT, montantTVA, montantTTC: montantHT + montantTVA };
};

// Montant en lettres (français)
export const numberToWordsFr = (num) => {
  const units = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix', 'onze', 'douze', 'treize', 'quatorze', 'quinze', 'seize', 'dix-sept', 'dix-huit', 'dix-neuf'];
  const tens = ['', '', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', 'soixante', 'quatre-vingt', 'quatre-vingt'];
  num = Math.floor(Math.abs(parseFloat(num) || 0));
  if (num === 0) return 'zéro';
  const below100 = (n) => {
    if (n < 20) return units[n];
    const t = Math.floor(n / 10);
    const u = n % 10;
    if (t === 7 || t === 9) {
      const rest = units[10 + u];
      return tens[t] + (t === 7 && u === 1 ? ' et ' : '-') + rest;
    }
    if (u === 0) return tens[t] + (t === 8 ? 's' : '');
    if (u === 1 && t !== 8) return tens[t] + ' et un';
    return tens[t] + '-' + units[u];
  };
  const below1000 = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    let out = '';
    if (h === 1) out = 'cent';
    else if (h > 1) out = units[h] + ' cent' + (r === 0 ? 's' : '');
    if (r > 0) out += (out ? ' ' : '') + below100(r);
    return out;
  };
  let words = '';
  if (num >= 1000000) {
    const m = Math.floor(num / 1000000);
    words += (m === 1 ? 'un million' : below1000(m) + ' millions');
    num %= 1000000;
    if (num > 0) words += ' ';
  }
  if (num >= 1000) {
    const k = Math.floor(num / 1000);
    words += (k === 1 ? 'mille' : below1000(k) + ' mille');
    num %= 1000;
    if (num > 0) words += ' ';
  }
  if (num > 0) words += below1000(num);
  return words.trim();
};

export const toCsv = (rows, headers) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.map(h => esc(h.label)).join(';')];
  rows.forEach((r) => lines.push(headers.map(h => esc(typeof h.value === 'function' ? h.value(r) : r[h.value])).join(';')));
  return '﻿' + lines.join('\n');
};

export const downloadText = (filename, text, mime = 'text/csv;charset=utf-8') => {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// Montant en lettres avec centimes, mots capitalisés (identique à METAFLOW)
export const amountToFrench = (amount) => {
  const totalCentimes = Math.round((parseFloat(amount) || 0) * 100);
  let integerPart = Math.floor(totalCentimes / 100);
  let centimes = totalCentimes % 100;
  if (centimes >= 100) { integerPart += Math.floor(centimes / 100); centimes %= 100; }
  let result = numberToWordsFr(integerPart);
  if (centimes > 0) result += ` virgule ${numberToWordsFr(centimes)}`;
  return result.split(' ').map((w) => w.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('-')).join(' ');
};

// « 1 234.56 » (format des modèles Word)
export const formatNumber2 = (n) => {
  const parts = Number(n || 0).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return parts.join('.');
};

// Frais du timbre (paiement en espèces) : 0,25 % du TTC
export const FRAIS_TIMBRE_RATE = 0.0025;
export const computeTimbre = (montantTTC, espece) => {
  const frais = espece ? Math.round(montantTTC * FRAIS_TIMBRE_RATE * 100) / 100 : 0;
  return { fraisTimbre: frais, totalFinal: montantTTC + frais };
};
